import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { properties } from "../../utils/properties";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from "recharts";

/* ─────────────────────────────────────────────────────────────
   CSS GLOBALE
   ───────────────────────────────────────────────────────────── */
const globalStyle = document.createElement("style");
globalStyle.textContent = `
  * { box-sizing: border-box; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes dot-pulse {
    0%, 80%, 100% { transform: scale(0.55); opacity: .35; }
    40%           { transform: scale(1);    opacity: 1;   }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .result-row:hover > td       { background: #F5FAF6 !important; }
  .history-item:hover          { background: #EDF5F0 !important; }
  .go-btn:hover                { background: #174022 !important; }
  .expand-btn:hover            { border-color: #2E6F40 !important; color: #2E6F40 !important; }
  .send-btn:hover:not(:disabled){ background: #174022 !important; }
  .suggestion-btn:hover        { border-color: #2E6F40 !important; color: #2E6F40 !important; }
  .debug-toggle:hover          { background: rgba(255,255,255,0.25) !important; }

  select, input, textarea, button { font-family: 'Roboto', system-ui, sans-serif; }
  select:focus, input:focus, textarea:focus { outline: none; }

  ::-webkit-scrollbar       { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #C3D8C9; border-radius: 10px; }
`;
document.head.appendChild(globalStyle);

/* ─────────────────────────────────────────────────────────────
   PALETTE
   ───────────────────────────────────────────────────────────── */
const C = {
  forest:    "#174022",
  green:     "#2E6F40",
  greenLt:   "#3D8A53",
  greenBg:   "#E8F3EC",
  greenLine: "#BED8C5",
  ink:       "#111814",
  slate:     "#445550",
  muted:     "#728078",
  ghost:     "#A8BCB2",
  bg:        "#F3F6F4",
  surface:   "#FFFFFF",
  border:    "#DDE8E1",
};

/* ─────────────────────────────────────────────────────────────
   PARSING
   ───────────────────────────────────────────────────────────── */
function parseSection(text, label) {
  if (!text || typeof text !== "string") return "";
  const marker = `==== ${label} ====`;
  const start  = text.indexOf(marker);
  if (start === -1) return "";
  const rest = text.substring(start + marker.length);
  const end  = rest.indexOf("====");
  return (end === -1 ? rest : rest.substring(0, end)).trim();
}

function parseResults(text) {
  if (!text || typeof text !== "string") return [];
  return text.split("\n")
    .map(l => l.trim())
    .filter(l => /^id:\s*\d+/.test(l))
    .map(line => {
      const obj = {};
      line.split(" | ").forEach(part => {
        const idx = part.indexOf(": ");
        if (idx === -1) return;
        obj[part.substring(0, idx).trim()] = part.substring(idx + 2).trim();
      });
      return obj;
    })
    .filter(r => r.id);
}

function parseMissioni(text) {
  if (!text) return [];
  return text.split("#*#").map(p => p.trim()).filter(Boolean)
    .map(p => p.split(":")[0].trim()).filter(Boolean);
}

function splitTokens(text) {
  if (!text) return [];
  return text.split(/[;,]/).map(t => t.trim()).filter(Boolean);
}

function fmtDate(d) {
  if (!d) return "";
  const p = d.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
}

function fmtEuro(v) {
  const n = Number(v || 0);
  return n === 0 ? "—" : "€\u00A0" + n.toLocaleString("it-IT", { minimumFractionDigits: 0 });
}

/* ─────────────────────────────────────────────────────────────
   OBIETTIVO COLORS
   ───────────────────────────────────────────────────────────── */
const OBJ_COLORS = {
  //PIANETA: { bg: "#D1FAE5", fg: "#065F46", dot: "#10B981" },
  PIANETA: { bg: "#C4DBF3", fg: "#000000", dot: "#008FD2" },
  
  //PERSONE: { bg: "#FEE2E2", fg: "#991B1B", dot: "#EF4444" },
  PERSONE: { bg: "#FFE9BF", fg: "#000000", dot: "#FBB900" },
  //CULTURA: { bg: "#EDE9FE", fg: "#4C1D95", dot: "#E50068" },
  CULTURA: { bg: "#FACCE1", fg: "#000000", dot: "#E50068" },
  DIREZIONE: { bg: "#D5F7E2", fg: "#000000", dot: "#046A38" }
};
function objStyle(s) {
  const k = Object.keys(OBJ_COLORS).find(k => (s || "").toUpperCase().includes(k));
  return k ? OBJ_COLORS[k] : { bg: "#ECEFF1", fg: "#37474F", dot: C.ghost };
}

/* ─────────────────────────────────────────────────────────────
   CHIP
   ───────────────────────────────────────────────────────────── */
function Chip({ text, bg, fg, dot }) {
  if (!text) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 11px", borderRadius: 20,
      background: bg, color: fg,
      fontSize: 13, fontWeight: 500,
      whiteSpace: "nowrap", lineHeight: 1.5,
    }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0 }} />}
      {text}
    </span>
  );
}

function ChipGroup({ label, items, bg, fg, dot }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={LABEL_S}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {items.map((t, i) => <Chip key={i} text={t} bg={bg} fg={fg} dot={dot} />)}
      </div>
    </div>
  );
}

const LABEL_S = {
  fontSize: 11, fontWeight: 700, color: C.green,
  textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left",
};

/* ─────────────────────────────────────────────────────────────
   SHARED DEBUG TOGGLE BUTTON
   ───────────────────────────────────────────────────────────── */
function DebugToggleBtn({ open, onClick, icon, label }) {
  return (
    <button
      className="debug-toggle"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: open ? C.greenBg : "transparent",
        border: `1px solid ${open ? C.greenLine : C.border}`,
        borderRadius: 20, padding: "5px 13px",
        cursor: "pointer", fontSize: 13,
        color: open ? C.green : C.muted,
        transition: "all .15s", fontWeight: open ? 600 : 400,
      }}
    >
      {icon}
      {open ? `Nascondi ${label}` : `Mostra ${label}`}
      <span style={{ fontSize: 10, marginLeft: 1 }}>{open ? "▲" : "▼"}</span>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
   DEBUG PANEL — SQL + LOG, stile chiaro e ordinato
   ───────────────────────────────────────────────────────────── */
function DebugPanel({ sql, fullLog }) {
  const [sqlOpen, setSqlOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const hasSql = !!sql;
  const hasLog = !!fullLog;
  if (!hasSql && !hasLog) return null;

  /* sezioni del log: split su ==== marker ==== */
  const logSections = hasLog ? parseLogSections(fullLog) : [];

  return (
    <div style={{ marginTop: 10, textAlign: "right" }}>

      {/* ── BOTTONI AFFIANCATI ── */}
      <div style={{ display: "inline-flex", gap: 8 }}>
        {hasSql && (
          <DebugToggleBtn
            open={sqlOpen}
            onClick={() => { setSqlOpen(!sqlOpen); setLogOpen(false); }}
            label="SQL"
            icon={
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <ellipse cx="6.5" cy="3" rx="5" ry="2"
                  stroke="currentColor" strokeWidth="1.3"/>
                <path d="M1.5 3v3.5c0 1.1 2.24 2 5 2s5-.9 5-2V3"
                  stroke="currentColor" strokeWidth="1.3"/>
                <path d="M1.5 6.5V10c0 1.1 2.24 2 5 2s5-.9 5-2V6.5"
                  stroke="currentColor" strokeWidth="1.3"/>
              </svg>
            }
          />
        )}
        {hasLog && (
          <DebugToggleBtn
            open={logOpen}
            onClick={() => { setLogOpen(!logOpen); setSqlOpen(false); }}
            label="Log"
            icon={
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <rect x="1.5" y="1.5" width="10" height="10" rx="2"
                  stroke="currentColor" strokeWidth="1.3"/>
                <path d="M4 4.5h5M4 6.5h5M4 8.5h3"
                  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            }
          />
        )}
      </div>

      {/* ── PANNELLO SQL ── */}
      {sqlOpen && (
        <div style={PANEL_STYLE}>
          <div style={PANEL_HEADER_STYLE}>
            <span style={PANEL_LABEL_STYLE}>Query SQL generata</span>
          </div>
          <div style={{
            fontFamily: "'Courier New', monospace",
            fontSize: 13, lineHeight: 1.9,
            color: C.slate, whiteSpace: "pre-wrap",
            wordBreak: "break-word", textAlign: "left",
          }}>
            {formatSql(sql)}
          </div>
        </div>
      )}

      {/* ── PANNELLO LOG ── */}
      {logOpen && (
        <div style={PANEL_STYLE}>
          <div style={PANEL_HEADER_STYLE}>
            <span style={PANEL_LABEL_STYLE}>Log pipeline</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            {logSections.map((sec, i) => (
              <LogSection key={i} title={sec.title} content={sec.content} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* stili pannello condivisi */
const PANEL_STYLE = {
  marginTop: 10,
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: "16px 18px",
  textAlign: "left",
  animation: "slideDown .2s ease",
  boxShadow: "0 2px 12px rgba(0,0,0,.06)",
};
const PANEL_HEADER_STYLE = {
  marginBottom: 12,
  paddingBottom: 10,
  borderBottom: `1px solid ${C.border}`,
};
const PANEL_LABEL_STYLE = {
  fontSize: 11, fontWeight: 700, color: C.muted,
  textTransform: "uppercase", letterSpacing: "0.08em",
};

/* singola sezione del log */
function LogSection({ title, content }) {
  /* sezioni senza contenuto reale le saltiamo */
  const trimmed = (content || "").trim();

  return (
    <div style={{
      borderLeft: `3px solid ${C.greenLine}`,
      paddingLeft: 12,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: C.green,
        textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: trimmed ? 5 : 0,
      }}>
        {title}
      </div>
      {trimmed && (
        <div style={{
          fontFamily: "'Courier New', monospace",
          fontSize: 12, lineHeight: 1.75, color: C.slate,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {trimmed}
        </div>
      )}
    </div>
  );
}

/* split del testo grezzo in sezioni { title, content } */
function parseLogSections(text) {
  if (!text) return [];
  const sections = [];
  const regex    = /==== (.+?) ====\n?/g;
  let match;
  let lastIndex  = 0;
  let lastTitle  = null;

  while ((match = regex.exec(text)) !== null) {
    if (lastTitle !== null) {
      sections.push({
        title:   lastTitle,
        content: text.substring(lastIndex, match.index),
      });
    }
    lastTitle = match[1].trim();
    lastIndex = match.index + match[0].length;
  }

  /* ultima sezione */
  if (lastTitle !== null) {
    sections.push({ title: lastTitle, content: text.substring(lastIndex) });
  }

  /* filtra RESULT (troppo lungo) e sezioni vuote non utili */
  return sections.filter(s => s.title !== "RESULT" && s.title !== "PARAMS");
}

/* formatta SQL con indentazione leggibile */
function formatSql(sql) {
  return sql
    .replace(/\s+AND\s+/g,  "\n    AND ")
    .replace(/\s+OR\s+/g,   "\n         OR ")
    .replace(/WHERE\s+1=1\s*/g, "WHERE 1=1\n    ")
    .replace(/ORDER BY/g,   "\nORDER BY")
    .replace(/LIMIT/g,      "\nLIMIT")
    .trim();
}

function formatDateToDDMMYYYY(dateStr) {
  if (!dateStr) return "";

  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";

  const [year, month, day] = parts;

  return `${day}${month}${year}`;
}

/* ─────────────────────────────────────────────────────────────
   RESULT ROW
   ───────────────────────────────────────────────────────────── */
function ResultRow({ r, navigate, idx }) {
  const [open, setOpen] = useState(false);
  const os       = objStyle(r.obiettivo);
  const missioni = parseMissioni(r.cosi_ripartito);
  const keywords = splitTokens(r.keywords);
  const entita   = splitTokens(r.entita);
  const persone  = splitTokens(r.persone);

  return (
    <>
      <tr
        className="result-row"
        style={{
          borderBottom: `1px solid ${C.border}`,
          animation: "fadeUp .3s ease both",
          animationDelay: `${idx * 30}ms`,
        }}
      >
        {/* DATA */}
        <td style={{ ...TD, width: 92, color: C.muted, fontSize: 14,
                     whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          {fmtDate(r.data_seduta)}
        </td>

        {/* INIZIATIVA */}
        <td style={{ ...TD, minWidth: 230, maxWidth: 340 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: C.ink, lineHeight: 1.4, marginBottom: 4 }}>
            {r.denominazione_iniziativa || "—"}
          </div>
          {r.localizzazione && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                           fontSize: 13, color: C.muted }}>
              <svg width="9" height="11" viewBox="0 0 9 11" fill="none">
                <path d="M4.5 0C2.57 0 1 1.57 1 3.5C1 6.13 4.5 11 4.5 11C4.5 11 8 6.13 8 3.5C8 1.57 6.43 0 4.5 0ZM4.5 5C3.67 5 3 4.33 3 3.5C3 2.67 3.67 2 4.5 2C5.33 2 6 2.67 6 3.5C6 4.33 5.33 5 4.5 5Z" fill={C.ghost}/>
              </svg>
              {r.localizzazione}
            </span>
          )}
        </td>

        {/* OBIETTIVO */}
        <td style={{ ...TD, width: 120 }}>
          <Chip text={r.obiettivo} bg={os.bg} fg={os.fg} dot={os.dot} />
        </td>

        {/* MISSIONE 
        <td style={{ ...TD, width: 180 }}>
          <Chip text={r.missione} bg={C.greenBg} fg={C.forest} dot={C.greenLt} />
        </td>        
      */}
        <td style={{ ...TD, width: 180 }}>
          <Chip text={r.missione} bg={os.bg} fg={os.fg} dot={os.dot} />
        </td>

        {/* ENTE */}
        <td style={{ ...TD, minWidth: 160, maxWidth: 210, fontSize: 14, color: C.slate, lineHeight: 1.5 }}>
          {r.ragione_sociale || "—"}
        </td>

        {/* IMPORTO */}
        <td style={{ ...TD, width: 130, fontWeight: 700, color: C.green, fontSize: 15,
                     fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {fmtEuro(r.importo_proposto)}
        </td>

        {/* AZIONI */}
        <td style={{ ...TD, width: 100 }}>
          <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
            <button
              className="expand-btn"
              title={open ? "Chiudi" : "Dettagli"}
              onClick={() => setOpen(!open)}
              style={{
                background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 6, padding: "5px 10px",
                cursor: "pointer", fontSize: 13, color: C.muted,
                transition: "all .15s",
              }}
            >
              {open ? "▲" : "▼"}
            </button>
            <button
              className="go-btn"
              onClick={() => {
                let where="/fascicoli/"+ formatDateToDDMMYYYY(r.data_seduta)+"/proposteintervento/schede/"+r.id_pratica;
                console.log(where); 
                navigate(where)
            }}
              style={{
                background: C.green, color: "#fff",
                border: "none", borderRadius: 6,
                padding: "6px 13px", cursor: "pointer",
                fontWeight: 700, fontSize: 13,
                transition: "background .15s",
                boxShadow: `0 1px 4px rgba(46,111,64,.25)`,
              }}
            >
              VAI
            </button>
          </div>
        </td>
      </tr>

      {/* ESPANSA */}
      {open && (
        <tr>
          <td colSpan={7} style={{
            background: "#F5FAF6",
            borderBottom: `1px solid ${C.greenLine}`,
            borderLeft: `4px solid ${C.green}`,
            padding: "20px 28px 22px",
            animation: "fadeUp .2s ease",
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "0 40px", textAlign: "left",
            }}>
              {/* sinistra: testi */}
              <div>
                {r.descrizione && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={LABEL_S}>Descrizione</div>
                    <p style={{ margin: "6px 0 0", fontSize: 15, color: C.slate,
                                lineHeight: 1.75, textAlign: "left" }}>
                      {r.descrizione}
                    </p>
                  </div>
                )}
                {r.ipotesi_dintervento && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={LABEL_S}>Ipotesi di intervento</div>
                    <p style={{ margin: "6px 0 0", fontSize: 15, color: C.slate,
                                lineHeight: 1.75, textAlign: "left" }}>
                      {r.ipotesi_dintervento}
                    </p>
                  </div>
                )}
              </div>

              {/* destra: chips */}
              <div>
                <ChipGroup label="Missioni coinvolte" items={missioni}
                  //bg="#E0F2FE" fg="#075985" dot="#0EA5E9" />
                  bg={"#FFFFFF"} fg={C.forest} dot={C.greenLt}/>
                <ChipGroup label="Persone coinvolte"  items={persone}
                  //bg="#FEF3C7" fg="#92400E" dot="#F59E0B" />
                  bg={"#FFFFFF"} fg={C.forest} dot={C.greenLt}/>
                <ChipGroup label="Entità coinvolte"   items={entita}                  
                  //bg="#FEF3C7" fg="#92400E" dot="#F59E0B" 
                  bg={"#FFFFFF"} fg={C.forest} dot={C.greenLt}/>
                <ChipGroup label="Keywords"           items={keywords}
                  //bg="#FEF3C7" fg="#92400E" dot="#F59E0B" 
                  bg={"#FFFFFF"} fg={C.forest} dot={C.greenLt}/>
              </div>
            </div>
          </td>
        </tr>
      )} 
    </>
  );
}

const TD = { padding: "12px 14px", verticalAlign: "top", textAlign: "left" };

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */
function EmptyState() {
  return (
    <div style={{ padding: "48px 0", textAlign: "left", animation: "fadeUp .4s ease" }}>
      <div style={{
        width: 54, height: 54, borderRadius: 14,
        background: C.greenBg, display: "flex",
        alignItems: "center", justifyContent: "center", marginBottom: 16,
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke={C.green} strokeWidth="1.8"/>
          <path d="M16.5 16.5L21 21" stroke={C.green} strokeWidth="1.8" strokeLinecap="round"/>
          <path d="M11 8v3M11 14h.01" stroke={C.green} strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.ink, marginBottom: 6 }}>
        Nessun risultato trovato
      </div>
      <div style={{ fontSize: 15, color: C.muted, lineHeight: 1.6 }}>
        Prova a riformulare la domanda o allargare i criteri di ricerca.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   PAGINA
   ───────────────────────────────────────────────────────────── */
export default function ChatPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const bottomRef = useRef(null);

  const [question,      setQuestion]      = useState("");
  const [chat,          setChat]          = useState(null);
  const [history,       setHistory]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [sortField,     setSortField]     = useState("data_seduta");
  const [sortDir,       setSortDir]       = useState("desc");

  // larghezza sidebar — costante, serve per il padding del main
  const SIDEBAR_W = 288;
  // altezza input bar — costante, serve per il padding-bottom del scroll area
  const INPUT_H   = 80;

  useEffect(() => { loadHistory(); if (id) loadChat(id); }, [id]);
  useEffect(() => {
    if (chat) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
  }, [chat]);

  async function loadHistory() {
    try {
      const res = await axios.get(
        properties.productionUrl + properties.productionUrlSuffix + "/core/rest/history.php",
        { withCredentials: true }
      );
      setHistory(res.data);
    } catch (e) { console.error(e); }
  }

  async function loadChat(chatId) {
    try {
      const res = await axios.get(
        properties.productionUrl + properties.productionUrlSuffix + `/core/rest/chat.php?id=${chatId}`,
        { withCredentials: true }
      );
      setChat(res.data);
    } catch (e) { console.error(e); }
  }

  async function sendQuestion() {
    if (!question.trim()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("question", question);
      const res = await axios.post(
        properties.productionUrl + properties.productionUrlSuffix + "/core/rest/ask.php",
        fd, { withCredentials: true }
      );
      setQuestion("");
      navigate(`/chat/${res.data.id}`);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendQuestion(); }
  }

  /* parsing */
  const resultsText = parseSection(chat?.response, "RESULT");
  const finalSql    = parseSection(chat?.response, "FINAL SQL");
  // log = tutto il response fino a ==== RESULT ==== (escluso, troppo verboso nella UI)
  const fullLog     = (() => {
    if (!chat?.response) return "";
    const idx = chat.response.indexOf("==== RESULT ====");
    return idx === -1 ? chat.response : chat.response.substring(0, idx);
  })();
  const results     = parseResults(resultsText);
  const countMatch  = resultsText?.match(/totale:\s*(\d+)/i);
  const countValue  = countMatch ? Number(countMatch[1]) : null;

  const sorted = [...results].sort((a, b) => {
    let va = a[sortField] || "", vb = b[sortField] || "";
    if (sortField === "importo_proposto") { va = Number(va); vb = Number(vb); }
    return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const filteredHistory = history.filter(h =>
    h.question.toLowerCase().includes(historySearch.toLowerCase())
  );

  const hasResults = countValue !== null || sorted.length > 0;

  const SUGGESTIONS = [
    "Quanti progetti della missione Attrattività in Piemonte nel 2024?",
    "Quali sono gli ultimi progetti finanziati della missione Abitare a Torino",
    "Restituisci l'ultimo contributo deliberato all'ente Camminare Insieme",
  ];

  return (
    /*
     * Layout:
     *  - SIDEBAR: position fixed, sinistra
     *  - MAIN scroll area: margin-left = SIDEBAR_W, padding-bottom = INPUT_H
     *  - INPUT BAR: position fixed, bottom, left = SIDEBAR_W
     *
     * In questo modo l'input bar è SEMPRE visibile indipendentemente
     * dallo scroll, e la sidebar non si sovrappone mai al contenuto.
     */
    <div style={{
      fontFamily: "'Roboto', system-ui, sans-serif",
      fontSize: 16, color: C.ink,
      background: C.bg,
      minHeight: "100vh",
    }}>

      {/* ══════════════════════════════════════════
          SIDEBAR — position: fixed
      ══════════════════════════════════════════ */}
      <div style={{
        position: "fixed",
        top: 0, left: 0, bottom: 0,
        width: SIDEBAR_W,
        background: C.surface,
        borderRight: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        zIndex: 100,
      }}>
        {/* brand */}
        <div style={{ padding: "18px 16px 14px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{
              width: 54, height: 54, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(140deg, ${C.green}, ${C.forest})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 3px 10px rgba(46,111,64,.3)`,
            }}>
              <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                <path d="M1 10L5.5 5L8.5 8L12 3.5L17 7"
                  stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1 }}>
                (AI)<sup>2</sup>
              </div>
              <div style={{ fontSize: 11, color: C.ink, marginTop: 3,
                            letterSpacing: "0.05em", textTransform: "uppercase" }}>
                AI a supporto di AI (Attività Istituzionale)
              </div>
            </div>
          </div>
        </div>

        {/* search */}
        <div style={{ padding: "10px 12px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ position: "relative" }}>
            <svg style={{ position: "absolute", left: 10, top: "50%",
                          transform: "translateY(-50%)", pointerEvents: "none" }}
              width="13" height="13" viewBox="0 0 13 13" fill="none">
              <circle cx="5.5" cy="5.5" r="4.5" stroke={C.ghost} strokeWidth="1.5"/>
              <path d="M9 9L12 12" stroke={C.ghost} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              placeholder="Cerca cronologia…"
              style={{
                width: "100%", padding: "8px 10px 8px 30px",
                borderRadius: 7, border: `1px solid ${C.border}`,
                fontSize: 14, background: C.bg, color: C.ink,
                transition: "border-color .2s",
              }}
              onFocus={e => e.target.style.borderColor = C.green}
              onBlur={e  => e.target.style.borderColor = C.border}
            />
          </div>
        </div>

        <div style={{ padding: "10px 16px 4px", fontSize: 11, fontWeight: 700,
                      color: C.ghost, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Cronologia
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 16px" }}>
          {filteredHistory.length === 0 && (
            <div style={{ fontSize: 14, color: C.ghost, padding: "12px 8px", textAlign: "left" }}>
              Nessuna conversazione
            </div>
          )}
          {filteredHistory.map(item => {
            const active = String(item.id) === String(id);
            return (
              <div
                key={item.id}
                className="history-item"
                onClick={() => navigate(`/chat/${item.id}`)}
                style={{
                  padding: "10px 12px", borderRadius: 8, marginBottom: 2,
                  cursor: "pointer", transition: "background .15s",
                  background: active ? C.greenBg : "transparent",
                  borderLeft: active ? `3px solid ${C.green}` : "3px solid transparent",
                }}
              >
                <div style={{
                  fontSize: 14, lineHeight: 1.45, textAlign: "left",
                  color: active ? C.green : C.ink,
                  fontWeight: active ? 600 : 400,
                  display: "-webkit-box", WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                  {item.question}
                </div>
                {item.created_at && (
                  <div style={{ fontSize: 12, color: C.ghost, marginTop: 3, textAlign: "left" }}>
                    {fmtDate(item.created_at?.substring(0, 10))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SCROLL AREA — margin-left per non finire sotto la sidebar
          padding-bottom per non finire sotto l'input bar
      ══════════════════════════════════════════ */}
      <div style={{
        marginLeft: SIDEBAR_W,
        paddingBottom: INPUT_H + 16,   /* spazio sufficiente sotto */
        minHeight: "100vh",
        padding: `28px 32px ${INPUT_H + 24}px`,
      }}>

        {/* welcome screen */}
        {!chat && !loading && (
          <div style={{ animation: "fadeUp .4s ease", textAlign: "left", paddingTop: 40 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: C.ink,
                          lineHeight: 1.2, marginBottom: 10 }}>
              Cosa vuoi scoprire?
            </div>
            <div style={{ fontSize: 16, color: C.muted, marginBottom: 28, lineHeight: 1.6 }}>
              Fai una domanda in linguaggio naturale sui progetti e le iniziative finanziate dalla Fondazione che sono passate dal Comitato di Gestione.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {SUGGESTIONS.map(s => (
                <button key={s} className="suggestion-btn"
                  onClick={() => setQuestion(s)}
                  style={{
                    background: C.surface, border: `1px solid ${C.border}`,
                    borderRadius: 22, padding: "9px 18px",
                    fontSize: 14, color: C.slate, cursor: "pointer",
                    boxShadow: "0 1px 4px rgba(0,0,0,.06)",
                    transition: "all .15s",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── BUBBLE DOMANDA — allineata a destra ── */}
        {chat && (
          <div style={{
            animation: "fadeUp .3s ease",
            marginBottom: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",   /* tutto a destra */
          }}>
            {/* bubble */}
            <div style={{
              background: `linear-gradient(135deg, ${C.green} 0%, ${C.forest} 100%)`,
              color: "#fff",
              padding: "13px 20px",
              borderRadius: "12px 12px 3px 12px",   /* angolo basso-destra = coda */
              maxWidth: 680, textAlign: "left",       /* testo interno a sinistra */
              boxShadow: `0 4px 16px rgba(46,111,64,.22)`,
              fontSize: 16, fontWeight: 500, lineHeight: 1.55,
            }}>
              {chat.question}
            </div>

            {/* debug SQL sotto la bubble, ancora allineato a destra */}
            <DebugPanel sql={finalSql} fullLog={fullLog} />
          </div>
        )}

        {/* loading */}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10,
                        color: C.muted, fontSize: 15, padding: "6px 0" }}>
            <div style={{ display: "flex", gap: 5 }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  display: "inline-block", width: 8, height: 8,
                  borderRadius: "50%", background: C.green,
                  animation: `dot-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
            Interrogazione del database in corso…
          </div>
        )}

        {/* empty */}
        {chat && !loading && !hasResults && resultsText !== "" && <EmptyState />}

        {/* ── RESULT CARD ── */}
        {hasResults && (
          <div style={{
            background: C.surface, borderRadius: 14,
            border: `1px solid ${C.border}`,
            boxShadow: "0 2px 16px rgba(0,0,0,.05)",
            overflow: "hidden", marginBottom: 20,
            animation: "fadeUp .4s ease",
          }}>

            {/* COUNT */}
            {countValue !== null && (
              <div style={{ padding: "26px 28px 20px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                  <span style={{ fontSize: 60, fontWeight: 800, color: C.green, lineHeight: 1 }}>
                    {countValue.toLocaleString("it-IT")}
                  </span>
                  <span style={{ fontSize: 18, color: C.muted, fontWeight: 300 }}>
                    progetti trovati
                  </span>
                </div>
                <div style={{ height: 100, marginTop: 16 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[{ name: "Risultati", value: countValue }]}
                      margin={{ top: 0, right: 0, left: -18, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 13, fill: C.muted }}
                             axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 13, fill: C.muted }}
                             axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                        cursor={{ fill: C.greenBg }}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        <Cell fill={C.green} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* TABLE */}
            {sorted.length > 0 && (
              <>
                {/* toolbar */}
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "14px 22px", borderBottom: `1px solid ${C.border}`,
                  gap: 10, flexWrap: "wrap",
                }}>
                  <div style={{ fontSize: 15, color: C.muted }}>
                    <span style={{ fontWeight: 700, color: C.green, fontSize: 18 }}>
                      {sorted.length}
                    </span>
                    {" "}risultati
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value={sortField} onChange={e => setSortField(e.target.value)}
                      style={{ padding: "7px 12px", borderRadius: 7,
                               border: `1px solid ${C.border}`, fontSize: 14,
                               background: C.bg, color: C.slate, cursor: "pointer" }}>
                      <option value="data_seduta">↕ Data</option>
                      <option value="importo_proposto">↕ Importo</option>
                      <option value="ragione_sociale">↕ Ente</option>
                    </select>
                    <select value={sortDir} onChange={e => setSortDir(e.target.value)}
                      style={{ padding: "7px 12px", borderRadius: 7,
                               border: `1px solid ${C.border}`, fontSize: 14,
                               background: C.bg, color: C.slate, cursor: "pointer" }}>
                      <option value="desc">↓ Decrescente</option>
                      <option value="asc">↑ Crescente</option>
                    </select>
                  </div>
                </div>

                {/* tabella */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
                    <thead>
                      <tr style={{ background: "#F1F6F2" }}>
                        {["Data", "Iniziativa", "Obiettivo", "Missione", "Ente", "Importo", ""].map(h => (
                          <th key={h} style={{
                            padding: "11px 14px", textAlign: "left",
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            letterSpacing: "0.07em", textTransform: "uppercase",
                            borderBottom: `2px solid ${C.greenLine}`,
                            whiteSpace: "nowrap",
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((r, i) => (
                        <ResultRow key={r.id} r={r} navigate={navigate} idx={i} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ══════════════════════════════════════════
          INPUT BAR — position: fixed, sempre visibile
      ══════════════════════════════════════════ */}
      <div style={{
        position: "fixed",
        bottom: 0,
        left: SIDEBAR_W,
        right: 0,
        zIndex: 100,
        borderTop: `1px solid ${C.border}`,
        padding: "12px 24px",
        background: C.surface,
        display: "flex",
        gap: 12,
        alignItems: "flex-end",
        boxShadow: "0 -3px 16px rgba(0,0,0,.07)",
      }}>
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Fai una domanda… (Invio per inviare)"
          rows={2}
          style={{
            flex: 1, borderRadius: 10,
            border: `1.5px solid ${C.border}`,
            padding: "11px 16px", fontSize: 16,
            resize: "none", lineHeight: 1.55,
            background: C.bg, color: C.ink,
            transition: "border-color .2s",
          }}
          onFocus={e => e.target.style.borderColor = C.green}
          onBlur={e  => e.target.style.borderColor = C.border}
        />
        <button
          className="send-btn"
          onClick={sendQuestion}
          disabled={loading}
          style={{
            padding: "13px 28px", borderRadius: 10, border: "none",
            background: loading ? C.ghost : C.green,
            color: "#fff", fontWeight: 700, fontSize: 16,
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background .15s", whiteSpace: "nowrap",
            boxShadow: loading ? "none" : `0 3px 10px rgba(46,111,64,.3)`,
          }}
        >
          {loading ? "…" : "Invia →"}
        </button>
      </div>
    </div>
  );
}
